import { useTranslate } from '@tolgee/react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Modal } from 'react-bootstrap';
import { generatePath } from 'react-router';
import { useMatch, useNavigate, useParams } from 'react-router-dom';

import ConfirmModal from '@/components/confirmModal';
import GlobalLoader from '@/components/globalLoader';
import { LomavisTemplateFolderModal } from '@/lomavisSharedUi/organisms/modals/LomavisTemplateFolderModal';
import type { TemplateFolderModalMode } from '@/lomavisSharedUi/organisms/modals/LomavisTemplateFolderModal';
import { LomavisTemplateMoveFolderModal } from '@/lomavisSharedUi/organisms/modals/LomavisTemplateMoveFolderModal';
import {
  LomavisOverlayEditor,
  type OverlayEditorExportActions,
  type OverlayEditorSavePayload
} from '@/lomavisSharedUi/templates/editors/LomavisOverlayEditor/LomavisOverlayEditor';
import {
  createDefaultOverlay,
  normalizeOverlay,
  type OverlayDefinition
} from '@/lomavisSharedUi/templates/editors/LomavisOverlayEditor/overlayDefinition';
import { LomavisOverlayEditorShell } from '@/lomavisSharedUi/templates/pages/LomavisOverlayEditorShell';
import { LomavisOverlayTemplateLibrary } from '@/lomavisSharedUi/templates/pages/LomavisOverlayTemplateLibrary';
import {
  uploadOverlayTemplateImage,
  type OverlayTemplateApiScope,
  type OverlayTemplateOrdering,
  type WritableOverlayTemplateApiScope
} from '@/api/overlayTemplates';
import {
  getOverlayTemplateFolderId,
  getOverlayTemplateFolderLibrary,
  type OverlayTemplateFolderLibrary,
  OverlayTemplateFolder,
  OverlayTemplateLibrarySelection,
  OverlayTemplateSortValue,
  OverlayTemplateSummary
} from '@/lomavisSharedUi/types/overlayTemplates';
import { getLocationDetails } from '@/redux/slices/location/locationDetails';
import {
  addOverlayTemplateFolder,
  fetchOverlayTemplateDetail,
  fetchNextOverlayTemplateFolders,
  fetchNextOverlayTemplates,
  fetchOverlayTemplateFolders,
  getOverlayTemplateFolderQueryKey,
  fetchOverlayTemplates,
  getOverlayTemplateQueryKey,
  moveOverlayTemplateToFolder,
  overlayTemplateActions,
  removeOverlayTemplate,
  removeOverlayTemplateFolder,
  renameOverlayTemplateFolder,
  saveOverlayTemplate,
  uploadOverlayTemplatePreview
} from '@/redux/slices/overlayTemplates';
import { useDispatch, useSelector } from '@/redux/store';
import { PATH_PAGE } from '@/routes/paths';
import { navigateToServerPage } from '@/utils/navigation';
import { usePlanFeature } from '@/utils/subscription/usePlanFeature';

import { OverlayTemplatesUpsell } from './OverlayTemplatesUpsell';
import { lomavisShowToastr } from '@/utils/ui/toastr';

/**
 * Width of the editor modal.
 *
 * Bootstrap has no `xxl` modal size, so this overrides its own
 * `--bs-modal-width` on the dialog rather than inventing a global class — the
 * same approach `OverlayTemplateSelectorModal` takes. Viewport-relative and
 * deliberately uncapped: the editor wants every pixel of canvas it can get, and
 * a fixed ceiling left the dialog at well under two thirds of a wide screen.
 * Paired with the body height below so the dialog reads as near-full-screen at
 * any size.
 */
const EDITOR_MODAL_WIDTH = '90vw';

/**
 * Height of the editor modal's body.
 *
 * The modal has to be given a definite height: the editor's workspace is
 * `flex-grow-1` with `minHeight: 0`, so in an auto-height dialog it would
 * collapse to nothing — and Pintura renders black when it is created against a
 * zero-sized root. Near-viewport so the dialog reads as full screen.
 */
const EDITOR_MODAL_BODY_HEIGHT = '90vh';

const WideEditorModalDialog = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof Modal.Dialog>
>(
  // react-bootstrap provides the dialog props through `dialogAs`.
  // eslint-disable-next-line react/prop-types
  ({ style, ...props }, ref) => {
    const dialogStyle: React.CSSProperties & { '--bs-modal-width': string } = {
      ...style,
      '--bs-modal-width': EDITOR_MODAL_WIDTH
    };

    return <Modal.Dialog {...props} ref={ref} style={dialogStyle} />;
  }
);

WideEditorModalDialog.displayName = 'WideEditorModalDialog';

const ORDERING_BY_SORT: Record<
  OverlayTemplateSortValue,
  OverlayTemplateOrdering
> = {
  newest: '-pk',
  oldest: 'pk',
  nameAsc: 'title',
  nameDesc: '-title'
};

const getIsSharedFromSelection = (
  selection: OverlayTemplateLibrarySelection,
  scope: OverlayTemplateApiScope
): boolean | undefined => {
  const folderLibrary = getOverlayTemplateFolderLibrary(selection);
  if (folderLibrary) return folderLibrary === 'shared';
  if (selection.startsWith('folder:')) {
    return scope.kind === 'account';
  }
  if (selection === 'shared') return true;
  if (selection === 'mine') return false;
  return undefined;
};

export const buildOverlayTemplateStartPostUrl = (
  locationSlug: string,
  templateUuid: string
) =>
  `${generatePath(PATH_PAGE.locationLomavisCreator, {
    locationslug: locationSlug
  })}?overlay_template_uuid=${encodeURIComponent(templateUuid)}`;

const getFieldMessage = (
  fieldErrors: Record<string, unknown>,
  field: string
) => {
  const value = fieldErrors[field];
  if (Array.isArray(value)) return String(value[0] ?? '');
  return typeof value === 'string' ? value : undefined;
};

const toSummary = (template: {
  uuid: string;
  title: string;
  thumbnail_url?: string | null;
  aspect_ratio: string;
  folder_uuid?: string | null;
  is_shared: boolean;
}): OverlayTemplateSummary => ({
  id: template.uuid,
  title: template.title,
  thumbnailUrl: template.thumbnail_url,
  aspectRatio: template.aspect_ratio,
  folderId: template.folder_uuid,
  isShared: template.is_shared
});

const toFolder = (folder: {
  uuid: string;
  name: string;
}): OverlayTemplateFolder => ({
  id: folder.uuid,
  name: folder.name
});

const createTemplateDefinitionId = () =>
  `overlay-template-${
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  }`;

const createEditorTemplate = (name = '') =>
  createDefaultOverlay(createTemplateDefinitionId(), name);

const getAspectRatio = (width: number, height: number) => {
  let left = Math.max(1, Math.round(width));
  let right = Math.max(1, Math.round(height));
  const originalLeft = left;
  const originalRight = right;
  while (right !== 0) [left, right] = [right, left % right];
  return `${originalLeft / left}:${originalRight / left}`;
};

const OverlayTemplateLibraryPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslate();
  const { locationslug, templateUuid: routeTemplateUuid } = useParams<{
    locationslug?: string;
    templateUuid?: string;
  }>();
  const accountRoute = Boolean(useMatch('/accounts/templates/*'));
  const pickerRoute = Boolean(
    useMatch(PATH_PAGE.profile_overlay_template_account_library)
  );
  // Creating is component state, not a route. A URL that lands straight on the
  // editor recreates Pintura during a page load, which is where the artboard
  // intermittently came up blank; requiring a click keeps every new overlay on
  // the settled-page path.
  const [creatingOverlay, setCreatingOverlay] = useState(false);
  const duplicateMatch = useMatch(
    accountRoute
      ? PATH_PAGE.account_overlay_template_duplicate
      : PATH_PAGE.profile_overlay_template_duplicate
  );
  const duplicateRoute = Boolean(duplicateMatch);
  const detailMatch = useMatch(
    accountRoute
      ? PATH_PAGE.account_overlay_template_detail
      : PATH_PAGE.profile_overlay_template_detail
  );
  // `/create/` is no longer a route, but the detail pattern would happily match
  // it as a template UUID and try to load an overlay called "create", leaving a
  // dead spinner. Send those links to the library instead, where the user picks
  // Create — which is the whole point of removing the route.
  const legacyCreatePath = detailMatch?.params.templateUuid === 'create';
  const detailRoute =
    !creatingOverlay &&
    !duplicateRoute &&
    !pickerRoute &&
    !legacyCreatePath &&
    Boolean(detailMatch);
  const templateUuid =
    creatingOverlay || pickerRoute
      ? undefined
      : routeTemplateUuid ??
        duplicateMatch?.params.templateUuid ??
        detailMatch?.params.templateUuid;
  const editorRoute = creatingOverlay || duplicateRoute || detailRoute;
  const libraryScopeKey = accountRoute
    ? 'account'
    : `${pickerRoute ? 'sharedPicker' : 'profile'}:${locationslug ?? ''}`;
  const location = useSelector((state) => state.locationDetails);
  const overlayTemplates = useSelector((state) => state.overlayTemplates);

  const [filterScopeKey, setFilterScopeKey] = useState(libraryScopeKey);
  const [selection, setSelection] = useState<OverlayTemplateLibrarySelection>(
    accountRoute || pickerRoute ? 'shared' : 'all'
  );
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] =
    useState<OverlayTemplateSortValue>('newest');
  const [folderModal, setFolderModal] = useState<{
    show: boolean;
    mode: TemplateFolderModalMode;
    folder?: OverlayTemplateFolder;
  }>({ show: false, mode: 'create' });
  const [folderName, setFolderName] = useState('');
  const [templateToDelete, setTemplateToDelete] =
    useState<OverlayTemplateSummary | null>(null);
  const [templateToMove, setTemplateToMove] =
    useState<OverlayTemplateSummary | null>(null);
  const [moveToFolderId, setMoveToFolderId] = useState<string | null>(null);
  const [editorFolderId, setEditorFolderId] = useState<string | null>(null);
  const [editorTemplate, setEditorTemplate] =
    useState<OverlayDefinition>(createEditorTemplate);
  const [editorExportActions, setEditorExportActions] =
    useState<OverlayEditorExportActions | null>(null);
  const [editorConfigurationError, setEditorConfigurationError] = useState<
    string | null
  >(null);
  const [isUploadingEditor, setIsUploadingEditor] = useState(false);
  const [isSavingEditor, setIsSavingEditor] = useState(false);
  const loadedEditorKeyRef = useRef<string | null>(null);
  const editorPreviewFileRef = useRef<File | null>(null);
  const editorSaveInFlightRef = useRef(false);

  const translate = useCallback(
    (key: string, fallback: string, params?: Record<string, string | number>) =>
      String(t(key, fallback, params)),
    [t]
  );

  useEffect(() => {
    if (!accountRoute && locationslug && location.slug !== locationslug) {
      dispatch(getLocationDetails(locationslug) as any);
    }
  }, [accountRoute, dispatch, location.slug, locationslug]);

  const scope = useMemo<OverlayTemplateApiScope | null>(() => {
    if (accountRoute) return { kind: 'account' };
    if (!location.uuid) return null;
    return { kind: 'profile', locationUuid: location.uuid };
  }, [accountRoute, location.uuid]);

  const writeScope = useMemo<WritableOverlayTemplateApiScope | null>(() => {
    if (accountRoute) return { kind: 'account' };
    if (!location.uuid) return null;
    return { kind: 'profile', locationUuid: location.uuid };
  }, [accountRoute, location.uuid]);

  const selectedFolderLibrary: OverlayTemplateFolderLibrary =
    getOverlayTemplateFolderLibrary(selection) ??
    (scope?.kind === 'account' || pickerRoute ? 'shared' : 'mine');
  const folderLibraryToLoad: OverlayTemplateFolderLibrary = templateToMove
    ? accountRoute
      ? 'shared'
      : 'mine'
    : selectedFolderLibrary;
  const shouldLoadFolders =
    editorRoute || selection !== 'all' || Boolean(templateToMove);
  const folderScope = useMemo<OverlayTemplateApiScope | null>(() => {
    if (!scope) return null;
    if (editorRoute) return writeScope;
    return scope;
  }, [editorRoute, scope, writeScope]);
  const currentFolderScopeKey =
    folderScope && shouldLoadFolders
      ? getOverlayTemplateFolderQueryKey(
          folderScope,
          folderLibraryToLoad === 'shared'
        )
      : null;
  const foldersBelongToCurrentScope =
    currentFolderScopeKey !== null &&
    overlayTemplates.loadedLibraryScopeKey === currentFolderScopeKey;
  const isFolderScopeLoading =
    currentFolderScopeKey !== null && !foldersBelongToCurrentScope;

  const filtersBelongToScope = filterScopeKey === libraryScopeKey;
  const selectedFolderId = getOverlayTemplateFolderId(selection);
  const selectedIsShared = scope
    ? getIsSharedFromSelection(selection, scope)
    : undefined;
  const templateListParams = useMemo(
    () =>
      scope
        ? {
            scope,
            isShared: selectedIsShared,
            folderUuid: selectedFolderId,
            search: searchValue.trim(),
            ordering: ORDERING_BY_SORT[sortValue]
          }
        : null,
    [scope, searchValue, selectedFolderId, selectedIsShared, sortValue]
  );
  const templateQueryKey = templateListParams
    ? getOverlayTemplateQueryKey(templateListParams)
    : null;

  useEffect(() => {
    if (filtersBelongToScope) return;
    setSelection(accountRoute || pickerRoute ? 'shared' : 'all');
    setSearchValue('');
    setSortValue('newest');
    setFilterScopeKey(libraryScopeKey);
  }, [accountRoute, filtersBelongToScope, libraryScopeKey, pickerRoute]);

  useEffect(() => {
    if (!folderScope || !shouldLoadFolders) return;
    void dispatch(
      fetchOverlayTemplateFolders({
        scope: folderScope,
        isShared: folderLibraryToLoad === 'shared'
      })
    );
  }, [dispatch, folderLibraryToLoad, folderScope, shouldLoadFolders]);

  useEffect(() => {
    if (!templateListParams || !filtersBelongToScope || editorRoute) return;
    const timeout = window.setTimeout(
      () => {
        void dispatch(fetchOverlayTemplates(templateListParams));
      },
      searchValue ? 250 : 0
    );
    return () => window.clearTimeout(timeout);
  }, [
    dispatch,
    editorRoute,
    filtersBelongToScope,
    searchValue,
    templateListParams
  ]);

  useEffect(() => {
    dispatch(overlayTemplateActions.clearActiveTemplate());
    loadedEditorKeyRef.current = null;
    if (creatingOverlay) {
      setEditorFolderId(selectedFolderId);
      setEditorTemplate(createEditorTemplate());
      setEditorConfigurationError(null);
      return;
    }
    if (editorRoute && templateUuid && scope) {
      void dispatch(fetchOverlayTemplateDetail({ uuid: templateUuid, scope }));
    }
  }, [
    creatingOverlay,
    dispatch,
    editorRoute,
    scope,
    selectedFolderId,
    templateUuid
  ]);

  useEffect(() => {
    const active = overlayTemplates.activeTemplate;
    if (!active || active.uuid !== templateUuid || creatingOverlay) return;
    const editorKey = `${active.uuid}:${duplicateRoute ? 'duplicate' : 'edit'}`;
    if (loadedEditorKeyRef.current === editorKey) return;
    loadedEditorKeyRef.current = editorKey;
    const nextName = duplicateRoute
      ? translate('overlayTemplates.copyOfTitle', '{title} copy', {
          title: active.title
        })
      : active.title;
    try {
      const loadedTemplate = normalizeOverlay(active.configuration);
      setEditorTemplate({
        ...loadedTemplate,
        id: duplicateRoute ? createTemplateDefinitionId() : loadedTemplate.id,
        name: nextName
      });
      setEditorConfigurationError(null);
    } catch (error) {
      setEditorTemplate(createEditorTemplate(nextName));
      setEditorConfigurationError(
        error instanceof Error
          ? error.message
          : translate(
              'overlayTemplates.editor.invalidConfiguration',
              'This overlay configuration cannot be loaded.'
            )
      );
    }
    setEditorFolderId(duplicateRoute ? null : active.folder_uuid ?? null);
  }, [
    creatingOverlay,
    duplicateRoute,
    overlayTemplates.activeTemplate,
    templateUuid,
    translate
  ]);

  const listPath = useMemo(
    () =>
      accountRoute
        ? PATH_PAGE.account_overlay_templates
        : generatePath(PATH_PAGE.profile_overlay_templates, {
            locationslug: locationslug ?? ''
          }),
    [accountRoute, locationslug]
  );

  useEffect(() => {
    if (legacyCreatePath) navigate(listPath, { replace: true });
  }, [legacyCreatePath, listPath, navigate]);

  const templatesBelongToCurrentQuery =
    templateQueryKey !== null &&
    overlayTemplates.activeLibraryQueryKey === templateQueryKey;
  const templates = useMemo(
    () =>
      templatesBelongToCurrentQuery
        ? overlayTemplates.templates.map(toSummary)
        : [],
    [overlayTemplates.templates, templatesBelongToCurrentQuery]
  );
  const folders = useMemo(
    () =>
      foldersBelongToCurrentScope ? overlayTemplates.folders.map(toFolder) : [],
    [foldersBelongToCurrentScope, overlayTemplates.folders]
  );

  const libraryLabels = useMemo(() => {
    const isPicker = pickerRoute;
    const isAccount = accountRoute;
    return {
      title: isPicker
        ? translate(
            'overlayTemplates.accountPicker.eyebrow',
            'Shared by the account'
          )
        : isAccount
        ? translate(
            'overlayTemplates.account.eyebrow',
            'Overlays · Account library'
          )
        : translate('overlayTemplates.profile.eyebrow', 'Overlays · {name}', {
            name: location.name
          }),
      description: isPicker
        ? translate(
            'overlayTemplates.accountPicker.description',
            'Choose an account overlay to start a post, or duplicate it into this profile group.'
          )
        : translate(
            'overlayTemplates.library.description',
            'Create and organize reusable overlays for social posts.'
          ),
      back: translate('common.back', 'Back'),
      createTemplate: translate(
        'overlayTemplates.createOverlay',
        'Create overlay'
      ),
      addFromAccountLibrary: translate(
        'overlayTemplates.addFromAccountLibrary',
        'Add from account library'
      ),
      search: translate('overlayTemplates.searchOverlays', 'Search overlays'),
      sort: translate('overlayTemplates.sortOverlays', 'Sort overlays'),
      emptyTitle: translate(
        'overlayTemplates.emptyOverlaysTitle',
        'No overlays yet'
      ),
      emptyDescription: translate(
        'overlayTemplates.emptyOverlaysDescription',
        'Create an overlay to start building your library.'
      ),
      retry: translate('common.retry', 'Retry'),
      loading: translate('common.loading', 'Loading'),
      templateCount: (count: number) =>
        translate('overlayTemplates.overlayCount', '{count} overlays', { count }),
      folderSidebar: {
        navigation: translate(
          'overlayTemplates.overlayFolderNavigation',
          'Overlay folders'
        ),
        title: translate('overlayTemplates.folders', 'Folders'),
        allTemplates: translate('overlayTemplates.allOverlays', 'All overlays'),
        sharedTemplates: translate(
          'overlayTemplates.sharedOverlays',
          'Shared overlays'
        ),
        myTemplates: translate('overlayTemplates.myOverlays', 'My overlays'),
        newFolder: translate('overlayTemplates.createFolder', 'Create folder'),
        folderActions: translate(
          'overlayTemplates.folderActions',
          'Folder actions'
        ),
        renameFolder: translate(
          'overlayTemplates.renameFolder',
          'Rename folder'
        ),
        deleteFolder: translate(
          'overlayTemplates.deleteFolder',
          'Delete folder'
        )
      },
      card: {
        shared: translate('overlayTemplates.shared', 'Shared'),
        sharedOverlay: translate(
          'overlayTemplates.sharedOverlay',
          'Shared overlay'
        ),
        edit: translate('common.edit', 'Edit'),
        changeFolder: translate(
          'overlayTemplates.changeFolder',
          'Change folder'
        ),
        duplicate: translate('overlayTemplates.duplicate', 'Duplicate'),
        delete: translate('common.delete', 'Delete'),
        startPost: translate('overlayTemplates.startPost', 'Start Post'),
        thumbnailLoading: translate(
          'overlayTemplates.thumbnailLoading',
          'Loading preview'
        ),
        thumbnailUnavailable: translate(
          'overlayTemplates.thumbnailUnavailable',
          'Preview unavailable'
        )
      }
    };
  }, [accountRoute, location.name, pickerRoute, translate]);

  const sortOptions = useMemo(
    () => [
      {
        value: 'newest' as const,
        label: translate('overlayTemplates.sortNewest', 'Newest first')
      },
      {
        value: 'oldest' as const,
        label: translate('overlayTemplates.sortOldest', 'Oldest first')
      },
      {
        value: 'nameAsc' as const,
        label: translate('overlayTemplates.sortNameAsc', 'Name A–Z')
      },
      {
        value: 'nameDesc' as const,
        label: translate('overlayTemplates.sortNameDesc', 'Name Z–A')
      }
    ],
    [translate]
  );

  const folderValidationMessage = getFieldMessage(
    overlayTemplates.fieldErrors,
    'name'
  );

  const openFolderModal = (
    mode: TemplateFolderModalMode,
    folder?: OverlayTemplateFolder
  ) => {
    dispatch(overlayTemplateActions.clearOverlayTemplateError());
    setFolderName(folder?.name ?? '');
    setFolderModal({ show: true, mode, folder });
  };

  const submitFolderModal = async () => {
    if (!writeScope) return;
    const args = { scope: writeScope };
    const action =
      folderModal.mode === 'create'
        ? addOverlayTemplateFolder({ ...args, name: folderName.trim() })
        : folderModal.mode === 'rename' && folderModal.folder
        ? renameOverlayTemplateFolder({
            ...args,
            uuid: folderModal.folder.id,
            name: folderName.trim()
          })
        : folderModal.folder
        ? removeOverlayTemplateFolder({
            ...args,
            uuid: folderModal.folder.id
          })
        : null;
    if (!action) return;
    try {
      await dispatch(action as any).unwrap();
      if (
        selectedFolderId === folderModal.folder?.id &&
        folderModal.mode === 'delete'
      ) {
        setSelection('all');
      }
      setFolderModal({ show: false, mode: 'create' });
    } catch {
      // Field errors are rendered from Redux state in the controlled modal.
    }
  };

  const openCreateEditor = () => setCreatingOverlay(true);

  /** Leaves the editor: clears create state, or returns from an editor route. */
  const closeEditor = () => {
    if (creatingOverlay) {
      setCreatingOverlay(false);
      return;
    }
    navigate(listPath);
  };

  const openEditEditor = (uuid: string) => {
    navigate(
      accountRoute
        ? generatePath(PATH_PAGE.account_overlay_template_detail, {
            templateUuid: uuid
          })
        : generatePath(PATH_PAGE.profile_overlay_template_detail, {
            locationslug: locationslug ?? '',
            templateUuid: uuid
          })
    );
  };

  const openDuplicateEditor = (uuid: string) => {
    navigate(
      accountRoute
        ? generatePath(PATH_PAGE.account_overlay_template_duplicate, {
            templateUuid: uuid
          })
        : generatePath(PATH_PAGE.profile_overlay_template_duplicate, {
            locationslug: locationslug ?? '',
            templateUuid: uuid
          })
    );
  };

  const saveEditor = async (template: OverlayDefinition = editorTemplate) => {
    if (!writeScope || !editorExportActions || editorSaveInFlightRef.current)
      return;
    editorSaveInFlightRef.current = true;
    setIsUploadingEditor(true);
    try {
      editorPreviewFileRef.current = null;
      await editorExportActions.exportJpg();
      const previewFile = editorPreviewFileRef.current;
      if (!previewFile) {
        lomavisShowToastr(
          translate(
            'overlayTemplates.previewGenerationFailed',
            'The preview image could not be generated. Please try again.'
          ),
          'error'
        );
        return;
      }
      const generatedPreviewImageUuid = await dispatch(
        uploadOverlayTemplatePreview({
          file: previewFile,
          locationUuid: accountRoute ? undefined : location.uuid
        })
      ).unwrap();
      setIsUploadingEditor(false);
      setIsSavingEditor(true);
      const payload = {
        title: template.name.trim(),
        folder_uuid: editorFolderId,
        configuration: template as unknown as Record<string, unknown>,
        preview_image_uuid: generatedPreviewImageUuid,
        aspect_ratio: getAspectRatio(
          template.canvas.width,
          template.canvas.height
        )
      };
      await dispatch(
        saveOverlayTemplate({
          uuid: detailRoute && !duplicateRoute ? templateUuid : undefined,
          scope: writeScope,
          payload
        })
      ).unwrap();
      lomavisShowToastr(
        translate('overlayTemplates.saved', 'Overlay saved.'),
        'success'
      );
      navigateToServerPage(listPath);
    } catch {
      // The editor reads field errors from Redux state.
    } finally {
      editorSaveInFlightRef.current = false;
      setIsUploadingEditor(false);
      setIsSavingEditor(false);
    }
  };

  const confirmTemplateDelete = async () => {
    if (!templateToDelete || !writeScope) return;
    await dispatch(
      removeOverlayTemplate({
        uuid: templateToDelete.id,
        scope: writeScope
      })
    ).unwrap();
    setTemplateToDelete(null);
  };

  const openMoveTemplateModal = (uuid: string) => {
    const template = templates.find((item) => item.id === uuid);
    if (!template) return;
    dispatch(overlayTemplateActions.clearOverlayTemplateError());
    setTemplateToMove(template);
    setMoveToFolderId(template.folderId ?? null);
  };

  const closeMoveTemplateModal = () => {
    if (overlayTemplates.movingTemplateUuid) return;
    setTemplateToMove(null);
    setMoveToFolderId(null);
    dispatch(overlayTemplateActions.clearOverlayTemplateError());
  };

  const submitMoveTemplate = async () => {
    if (!templateToMove || !writeScope) return;
    await dispatch(
      moveOverlayTemplateToFolder({
        uuid: templateToMove.id,
        folderUuid: moveToFolderId,
        scope: writeScope
      })
    ).unwrap();
    setTemplateToMove(null);
    setMoveToFolderId(null);
    if (templateListParams) {
      await dispatch(fetchOverlayTemplates(templateListParams));
    }
    lomavisShowToastr(
      translate('overlayTemplates.folderChanged', 'Overlay folder changed.'),
      'success'
    );
  };

  const startPost = (uuid: string) => {
    if (!locationslug) return;
    window.location.assign(
      buildOverlayTemplateStartPostUrl(locationslug, uuid)
    );
  };

  const loadMoreFolders = useCallback(() => {
    if (
      !folderScope ||
      !currentFolderScopeKey ||
      !foldersBelongToCurrentScope ||
      !overlayTemplates.folderNext
    )
      return;
    void dispatch(
      fetchNextOverlayTemplateFolders({
        scope: folderScope,
        isShared: folderLibraryToLoad === 'shared',
        scopeKey: currentFolderScopeKey,
        nextUrl: overlayTemplates.folderNext
      })
    );
  }, [
    currentFolderScopeKey,
    dispatch,
    folderLibraryToLoad,
    folderScope,
    foldersBelongToCurrentScope,
    overlayTemplates.folderNext
  ]);

  if (!scope || (!accountRoute && location.isLoading)) return <GlobalLoader />;

  // The editor is rendered inside a modal over the library rather than in
  // place of it, so Pintura is created on a page that has already loaded and
  // painted. Opening it no longer reloads the Django page either — see
  // `openCreateEditor`. Both together are what keep the artboard reliable:
  // created during a page load it intermittently comes up blank, with correct
  // state and geometry but nothing ever presented to the screen.
  const renderEditor = () => {
    if (
      !creatingOverlay &&
      (overlayTemplates.loadingTemplate ||
        overlayTemplates.activeTemplate?.uuid !== templateUuid)
    )
      return <GlobalLoader />;
    const editorMode = duplicateRoute
      ? 'duplicate'
      : creatingOverlay
      ? 'create'
      : 'edit';
    return (
      <LomavisOverlayEditorShell
        mode={editorMode}
        templateName={editorTemplate.name}
        folderId={editorFolderId}
        folders={folders}
        templateUuid={editorMode === 'duplicate' ? null : templateUuid ?? null}
        configuration={editorTemplate as unknown as Record<string, unknown>}
        scopeLabel={
          accountRoute
            ? translate('overlayTemplates.accountLibrary', 'Account library')
            : location.name
        }
        labels={{
          title:
            editorMode === 'create'
              ? translate(
                  'overlayTemplates.editor.createTitle',
                  'Create overlay'
                )
              : editorMode === 'duplicate'
              ? translate(
                  'overlayTemplates.editor.duplicateTitle',
                  'Duplicate overlay'
                )
              : translate('overlayTemplates.editor.editTitle', 'Edit overlay'),
          description: translate(
            'overlayTemplates.editor.description',
            'Name and organize the overlay. The visual editor is loaded in the workspace.'
          ),
          templateName: translate(
            'overlayTemplates.overlayName',
            'Overlay name'
          ),
          folder: translate('overlayTemplates.folder', 'Folder'),
          noFolder: translate('overlayTemplates.noFolder', 'No folder'),
          cancel: translate('common.cancel', 'Cancel'),
          save: translate('common.save', 'Save'),
          saving: translate('common.saving', 'Saving'),
          editorRegion: translate(
            'overlayTemplates.editor.region',
            'Overlay editor'
          ),
          editorPlaceholder: translate(
            'overlayTemplates.editor.placeholder',
            'The overlay editor is provided separately and mounts in this workspace.'
          ),
          templateUuid: translate(
            'overlayTemplates.editor.templateUuid',
            'Overlay UUID'
          ),
          configuration: translate(
            'overlayTemplates.editor.configuration',
            'Loaded configuration JSON'
          ),
          unassignedUuid: translate(
            'overlayTemplates.editor.unassignedUuid',
            'Assigned after the overlay is saved'
          )
        }}
        templateNameError={getFieldMessage(
          overlayTemplates.fieldErrors,
          'title'
        )}
        configurationError={
          getFieldMessage(overlayTemplates.fieldErrors, 'configuration') ??
          overlayTemplates.error ??
          editorConfigurationError ??
          undefined
        }
        canSave={Boolean(editorTemplate.name.trim())}
        integratedEditor
        onTemplateNameChange={(name) =>
          setEditorTemplate((template) => ({ ...template, name }))
        }
        onFolderChange={setEditorFolderId}
        onCancel={closeEditor}
        onSave={() => {
          saveEditor().catch(() => undefined);
        }}
      >
        <LomavisOverlayEditor
          value={editorTemplate}
          onChange={setEditorTemplate}
          onBack={closeEditor}
          saveLabel={translate('overlayTemplates.editor.save', 'Save overlay')}
          saveFormat="json"
          showDownloadButton
          onExportActionsReady={setEditorExportActions}
          templateNameError={getFieldMessage(
            overlayTemplates.fieldErrors,
            'title'
          )}
          isUploading={isUploadingEditor || overlayTemplates.uploadingPreview}
          isSaving={isSavingEditor || overlayTemplates.savingTemplate}
          onExportImage={(file, options) => {
            if (options.format === 'jpg') editorPreviewFileRef.current = file;
          }}
          onUploadImage={async (file) =>
            await uploadOverlayTemplateImage({
              file,
              locationUuid: accountRoute ? undefined : location.uuid
            })
          }
          onSave={(payload: OverlayEditorSavePayload) => {
            if (payload.format !== 'json') return;
            setEditorTemplate(payload.template);
            saveEditor(payload.template).catch(() => undefined);
          }}
        />
      </LomavisOverlayEditorShell>
    );
  };

  return (
    <>
      <LomavisOverlayTemplateLibrary
        mode={
          pickerRoute
            ? 'accountPicker'
            : accountRoute
            ? 'account'
            : 'profileGroup'
        }
        templates={templates}
        folders={folders}
        selection={selection}
        folderLibrary={selectedFolderLibrary}
        searchValue={searchValue}
        sortValue={sortValue}
        sortOptions={sortOptions}
        labels={libraryLabels}
        showPageHeading={false}
        showBackAction={pickerRoute}
        showCreateAction={!pickerRoute}
        canManageFolders={
          !pickerRoute && (accountRoute || selectedFolderLibrary === 'mine')
        }
        templateCount={
          templatesBelongToCurrentQuery ? overlayTemplates.count : 0
        }
        isLoading={
          !templatesBelongToCurrentQuery || overlayTemplates.loadingLibrary
        }
        hasMoreTemplates={
          templatesBelongToCurrentQuery && Boolean(overlayTemplates.next)
        }
        isLoadingMore={overlayTemplates.loadingMoreTemplates}
        loadMoreErrorMessage={overlayTemplates.loadMoreError}
        isFolderLoading={
          isFolderScopeLoading || overlayTemplates.loadingFolders
        }
        hasMoreFolders={
          foldersBelongToCurrentScope && Boolean(overlayTemplates.folderNext)
        }
        isFolderLoadingMore={overlayTemplates.loadingMoreFolders}
        folderErrorMessage={overlayTemplates.folderError}
        folderLoadMoreErrorMessage={overlayTemplates.folderLoadMoreError}
        errorMessage={
          folderModal.show && folderValidationMessage
            ? null
            : overlayTemplates.error
        }
        onBack={() => navigate(listPath)}
        onCreateTemplate={openCreateEditor}
        onSearchChange={setSearchValue}
        onSortChange={setSortValue}
        onSelectionChange={setSelection}
        onCreateFolder={() => openFolderModal('create')}
        onRenameFolder={(folder) => openFolderModal('rename', folder)}
        onDeleteFolder={(folder) => openFolderModal('delete', folder)}
        onEditTemplate={pickerRoute ? undefined : openEditEditor}
        onChangeTemplateFolder={pickerRoute ? undefined : openMoveTemplateModal}
        onDuplicateTemplate={openDuplicateEditor}
        onDeleteTemplate={
          pickerRoute
            ? undefined
            : (uuid) =>
                setTemplateToDelete(
                  templates.find((template) => template.id === uuid) ?? null
                )
        }
        onStartPost={accountRoute ? undefined : startPost}
        onLoadMore={() => {
          if (!templateQueryKey || !overlayTemplates.next) return;
          void dispatch(
            fetchNextOverlayTemplates({
              queryKey: templateQueryKey,
              nextUrl: overlayTemplates.next
            })
          );
        }}
        onLoadMoreFolders={loadMoreFolders}
        onRetryLoadMore={() => {
          if (!templateQueryKey || !overlayTemplates.next) return;
          void dispatch(
            fetchNextOverlayTemplates({
              queryKey: templateQueryKey,
              nextUrl: overlayTemplates.next
            })
          );
        }}
        onRetryFolders={() => {
          if (!folderScope || !shouldLoadFolders) return;
          void dispatch(
            fetchOverlayTemplateFolders({
              scope: folderScope,
              isShared: folderLibraryToLoad === 'shared',
              force: true
            })
          );
        }}
        onRetryLoadMoreFolders={loadMoreFolders}
        onRetry={() => {
          if (!templateListParams) return;
          dispatch(fetchOverlayTemplates(templateListParams)).catch(
            () => undefined
          );
        }}
      />

      <LomavisTemplateFolderModal
        show={folderModal.show}
        mode={folderModal.mode}
        name={folderName}
        targetName={folderModal.folder?.name}
        labels={{
          createTitle: translate(
            'overlayTemplates.folder.createTitle',
            'Create folder'
          ),
          renameTitle: translate(
            'overlayTemplates.folder.renameTitle',
            'Rename folder'
          ),
          deleteTitle: translate(
            'overlayTemplates.folder.deleteTitle',
            'Delete folder'
          ),
          folderName: translate('overlayTemplates.folder.name', 'Folder name'),
          cancel: translate('common.cancel', 'Cancel'),
          create: translate('common.create', 'Create'),
          save: translate('common.save', 'Save'),
          delete: translate('common.delete', 'Delete'),
          deleteMessage: translate(
            'overlayTemplates.folder.deleteFolderAndContentsMessage',
            'Are you sure you want to delete this folder? All overlays inside it will be permanently deleted. This action cannot be undone.'
          )
        }}
        validationMessage={folderValidationMessage}
        isSubmitting={overlayTemplates.mutatingFolder}
        onNameChange={setFolderName}
        onCancel={() => setFolderModal({ show: false, mode: 'create' })}
        onSubmit={() => {
          submitFolderModal().catch(() => undefined);
        }}
      />

      <LomavisTemplateMoveFolderModal
        show={Boolean(templateToMove)}
        templateTitle={templateToMove?.title}
        folderId={moveToFolderId}
        initialFolderId={templateToMove?.folderId ?? null}
        folders={folders}
        labels={{
          title: translate(
            'overlayTemplates.changeFolderTitle',
            'Change folder'
          ),
          template: translate('overlayTemplates.template', 'Overlay'),
          folder: translate('overlayTemplates.folder', 'Folder'),
          noFolder: translate('overlayTemplates.noFolder', 'No folder'),
          cancel: translate('common.cancel', 'Cancel'),
          save: translate('common.save', 'Save'),
          saving: translate('common.saving', 'Saving'),
          loadMoreFolders: translate(
            'overlayTemplates.folder.loadMore',
            'Load more folders'
          ),
          loadingFolders: translate(
            'overlayTemplates.folder.loading',
            'Loading folders'
          ),
          retry: translate('common.retry', 'Retry')
        }}
        validationMessage={getFieldMessage(
          overlayTemplates.fieldErrors,
          'folder_uuid'
        )}
        isSubmitting={Boolean(overlayTemplates.movingTemplateUuid)}
        hasMoreFolders={
          foldersBelongToCurrentScope && Boolean(overlayTemplates.folderNext)
        }
        isLoadingMoreFolders={
          isFolderScopeLoading ||
          overlayTemplates.loadingFolders ||
          overlayTemplates.loadingMoreFolders
        }
        loadMoreErrorMessage={overlayTemplates.folderLoadMoreError}
        onFolderChange={setMoveToFolderId}
        onCancel={closeMoveTemplateModal}
        onSubmit={() => {
          submitMoveTemplate().catch(() => undefined);
        }}
        onLoadMoreFolders={loadMoreFolders}
        onRetryLoadMoreFolders={loadMoreFolders}
      />

      <Modal
        show={editorRoute}
        size="xl"
        dialogAs={WideEditorModalDialog}
        // The editor paints to the edge of a padding-less body, so without
        // clipping here its square corners cover the dialog's rounded ones.
        // Bootstrap's default radius is ~6px, which disappears on a dialog this
        // large, so step it up one token.
        contentClassName="overflow-hidden rounded-4"
        centered
        backdrop="static"
        keyboard={false}
        onHide={closeEditor}
        aria-label={translate(
          'overlayTemplates.editor.region',
          'Overlay editor'
        )}
      >
        <Modal.Body
          className="p-0 d-flex flex-column overflow-hidden"
          style={{ height: EDITOR_MODAL_BODY_HEIGHT }}
        >
          {renderEditor()}
        </Modal.Body>
      </Modal>

      <ConfirmModal
        show={Boolean(templateToDelete)}
        handleClose={() => setTemplateToDelete(null)}
        handleConfirm={() => {
          confirmTemplateDelete().catch(() => undefined);
        }}
        title={translate('overlayTemplates.deleteTitle', 'Delete overlay')}
        message={translate(
          'overlayTemplates.deleteMessage',
          'This overlay will be permanently deleted.'
        )}
        showNextButton={false}
        confirmDisabled={Boolean(overlayTemplates.deletingTemplateUuid)}
      />
    </>
  );
};

/**
 * Overlays are a paid plan feature. The gate mounts the library only once the
 * plan is known to include it, so an account without the feature never fires
 * the library's requests — the API would refuse them anyway — and gets the
 * explainer instead.
 */
const OverlayTemplatesPage: React.FC = () => {
  const { isAvailable, isLoading } = usePlanFeature('overlay_templates');

  if (isLoading) return <GlobalLoader />;

  if (!isAvailable) return <OverlayTemplatesUpsell />;

  return <OverlayTemplateLibraryPage />;
};

export default OverlayTemplatesPage;
